/*
LIME LABS COMMERCE LAYER
Component: PDP V5.1 Interaction Controller
Project: La Troupe
Version: 5.1
Status: LLCL-QA

Progressive enhancement only.
- Reads Agent C's resulting visible native gallery cells; never filters/reorders media.
- Classifies visual aspect ratio for portrait-first composition.
- Keeps horizontal manual gallery navigation; hero autoplay remains OFF.
- Localizes Size Guide labels without changing radio/tab semantics.
- Runs the related rail drift only after content exists, with full pause/reduced-motion rules.
*/

(() => {
  'use strict';

  if (window.__LLCL_PDP_V51__) return;
  window.__LLCL_PDP_V51__ = true;

  const PRODUCT_SECTION = '.shopify-section--main-product';
  const RELATED_ROOT = 'product-recommendations.ll-pdp-v4-related';
  const RELATED_RAIL = '[data-ll-pdp-v5-related-rail]';
  const RELATED_CARD = 'product-card.product-card';
  const RELATED_SPEED = 7;
  const INITIAL_RELATED_PAUSE_MS = 2200;
  const USER_PAUSE_MS = 5000;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const pad = value => String(Math.max(0, value)).padStart(2, '0');
  const locale = () => (document.documentElement.lang || 'es').split('-')[0].toLowerCase();
  const isEnglish = () => locale() === 'en';

  /* ========================================================
     01. GALLERY — READ-ONLY CONSUMER OF NATIVE/AGENT C STATE
     ======================================================== */

  const galleryCarousel = gallery => (
    gallery?.carousel || gallery?.querySelector?.('scroll-carousel.product-gallery__carousel') || null
  );

  const visibleGalleryCells = gallery => {
    const nativeCells = Array.from(gallery?.carousel?.cells || []);
    if (nativeCells.length) return nativeCells.filter(cell => cell instanceof Element);

    return Array.from(gallery?.querySelectorAll?.('.product-gallery__media:not([hidden])') || []);
  };

  const closestCellIndex = (carousel, cells) => {
    if (!carousel || !cells.length) return 0;

    const position = carousel.scrollLeft || 0;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    cells.forEach((cell, index) => {
      const distance = Math.abs((cell.offsetLeft || 0) - position);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  };

  const classifyMediaCell = cell => {
    if (!(cell instanceof Element)) return;

    cell.classList.remove(
      'is-ll-pdp-v51-portrait',
      'is-ll-pdp-v51-square',
      'is-ll-pdp-v51-landscape'
    );

    const image = cell.querySelector('img');
    if (!image) {
      cell.classList.add('is-ll-pdp-v51-landscape');
      return;
    }

    const width = image.naturalWidth || Number(image.getAttribute('width')) || image.clientWidth || 0;
    const height = image.naturalHeight || Number(image.getAttribute('height')) || image.clientHeight || 0;

    if (!width || !height) {
      image.addEventListener('load', () => classifyMediaCell(cell), { once: true });
      return;
    }

    const ratio = width / height;

    if (ratio < 0.90) {
      cell.classList.add('is-ll-pdp-v51-portrait');
    } else if (ratio > 1.12) {
      cell.classList.add('is-ll-pdp-v51-landscape');
    } else {
      cell.classList.add('is-ll-pdp-v51-square');
    }
  };

  const createGalleryDeck = gallery => {
    let deck = gallery.querySelector('.ll-pdp-v5-gallery-deck');
    if (deck) return deck;

    deck = document.createElement('div');
    deck.className = 'll-pdp-v5-gallery-deck';
    deck.setAttribute('aria-label', isEnglish() ? 'Product media navigation' : 'Navegación de imágenes del producto');
    deck.innerHTML = `
      <div class="ll-pdp-v5-gallery-deck__status" aria-live="polite">
        <span class="ll-pdp-v5-gallery-deck__current">01</span>
        <span aria-hidden="true">/</span>
        <span class="ll-pdp-v5-gallery-deck__total">01</span>
      </div>
      <div class="ll-pdp-v5-gallery-deck__progress" aria-hidden="true"><i></i></div>
      <div class="ll-pdp-v5-gallery-deck__controls">
        <button type="button" class="ll-pdp-v5-gallery-deck__button" data-ll-pdp-v51-gallery-prev aria-label="${isEnglish() ? 'Previous view' : 'Vista anterior'}">←</button>
        <button type="button" class="ll-pdp-v5-gallery-deck__button" data-ll-pdp-v51-gallery-next aria-label="${isEnglish() ? 'Next view' : 'Vista siguiente'}">→</button>
      </div>
    `;

    const imageList = gallery.querySelector('.product-gallery__image-list');
    if (imageList) imageList.insertAdjacentElement('afterend', deck);
    else gallery.append(deck);

    return deck;
  };

  const updateGalleryDeck = gallery => {
    if (!gallery) return;

    const cells = visibleGalleryCells(gallery);
    const carousel = galleryCarousel(gallery);
    const deck = gallery.querySelector('.ll-pdp-v5-gallery-deck');
    if (!deck) return;

    const current = deck.querySelector('.ll-pdp-v5-gallery-deck__current');
    const totalNode = deck.querySelector('.ll-pdp-v5-gallery-deck__total');
    const progress = deck.querySelector('.ll-pdp-v5-gallery-deck__progress > i');
    const prev = deck.querySelector('[data-ll-pdp-v51-gallery-prev]');
    const next = deck.querySelector('[data-ll-pdp-v51-gallery-next]');
    const total = cells.length;
    const index = closestCellIndex(carousel, cells);

    if (current) current.textContent = pad(total ? index + 1 : 0);
    if (totalNode) totalNode.textContent = pad(total);
    if (progress) progress.style.transform = `scaleX(${total <= 1 ? 1 : index / (total - 1)})`;
    if (prev) prev.disabled = total <= 1 || index <= 0;
    if (next) next.disabled = total <= 1 || index >= total - 1;
  };

  const syncGalleryStructure = gallery => {
    if (!gallery) return;

    gallery.querySelectorAll('.ll-pdp-v42-media-marker, .ll-pdp-v5-media-marker, .ll-pdp-v51-media-marker').forEach(marker => marker.remove());

    const cells = visibleGalleryCells(gallery);
    const total = cells.length;
    const labelText = isEnglish() ? 'PRODUCT / VIEW' : 'PRODUCTO / VISTA';

    cells.forEach((cell, index) => {
      classifyMediaCell(cell);

      const marker = document.createElement('div');
      marker.className = 'll-pdp-v51-media-marker';
      marker.setAttribute('aria-hidden', 'true');
      marker.innerHTML = `
        <span class="ll-pdp-v51-media-marker__label">${labelText}</span>
        <span class="ll-pdp-v51-media-marker__index">${pad(index + 1)} / ${pad(total)}</span>
      `;
      cell.append(marker);
    });

    createGalleryDeck(gallery);
    gallery.classList.add('is-ll-pdp-v5-ready');
    updateGalleryDeck(gallery);
  };

  const bindGallery = section => {
    if (!section) return;

    const gallery = section.querySelector('product-gallery');
    if (!gallery) return;

    const carousel = galleryCarousel(gallery);
    const deck = createGalleryDeck(gallery);

    if (gallery.dataset.llPdpV51Bound !== 'true') {
      gallery.dataset.llPdpV51Bound = 'true';

      carousel?.addEventListener('scroll', () => updateGalleryDeck(gallery), { passive: true });

      deck.querySelector('[data-ll-pdp-v51-gallery-prev]')?.addEventListener('click', () => {
        const cells = visibleGalleryCells(gallery);
        const index = closestCellIndex(carousel, cells);
        const target = cells[Math.max(0, index - 1)];
        if (!carousel || !target) return;

        carousel.scrollTo({
          left: target.offsetLeft,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      });

      deck.querySelector('[data-ll-pdp-v51-gallery-next]')?.addEventListener('click', () => {
        const cells = visibleGalleryCells(gallery);
        const index = closestCellIndex(carousel, cells);
        const target = cells[Math.min(cells.length - 1, index + 1)];
        if (!carousel || !target) return;

        carousel.scrollTo({
          left: target.offsetLeft,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      });

      if ('ResizeObserver' in window && carousel) {
        const observer = new ResizeObserver(() => updateGalleryDeck(gallery));
        observer.observe(carousel);
      }
    }

    syncGalleryStructure(gallery);
  };

  const scheduleGallerySync = section => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => bindGallery(section || document.querySelector(PRODUCT_SECTION)));
    });
  };

  const syncAllGalleries = () => {
    document.querySelectorAll(PRODUCT_SECTION).forEach(scheduleGallerySync);
  };

  /* ========================================================
     02. SIZE GUIDE LOCALIZATION — SEMANTICS UNCHANGED
     ======================================================== */

  const syncSizeGuideLabels = (scope = document) => {
    const english = isEnglish();

    scope.querySelectorAll?.('.ll-pdp-v4-details .lt-tab-labels').forEach(group => {
      const womenText = english ? 'WOMEN' : 'MUJER';
      const menText = english ? 'MEN' : 'HOMBRE';

      group.querySelectorAll('label[for="lt-tab-mujer"], label[for="lt-tab-women"]').forEach(label => {
        label.textContent = womenText;
        label.setAttribute('aria-label', womenText);
      });

      group.querySelectorAll('label[for="lt-tab-hombre"], label[for="lt-tab-men"]').forEach(label => {
        label.textContent = menText;
        label.setAttribute('aria-label', menText);
      });
    });
  };

  /* ========================================================
     03. RELATED — VISIBLE CARDS + SLOW ACCESSIBLE DRIFT
     ======================================================== */

  const bindRelatedRail = root => {
    if (!root || root.dataset.llPdpV51Bound === 'true') return;

    const rail = root.querySelector(RELATED_RAIL);
    if (!rail) return;

    const cards = () => Array.from(rail.querySelectorAll(RELATED_CARD));
    if (!cards().length) return;

    const prev = root.querySelector('[data-ll-pdp-v5-related-prev]');
    const next = root.querySelector('[data-ll-pdp-v5-related-next]');
    const current = root.querySelector('[data-ll-pdp-v5-related-current]');
    const totalNode = root.querySelector('[data-ll-pdp-v5-related-total]');
    const progress = root.querySelector('[data-ll-pdp-v5-related-progress]');
    const heading = root.querySelector('.ll-pdp-v4-related__heading');

    root.dataset.llPdpV51Bound = 'true';
    rail.classList.add('ll-pdp-v51-related__rail');

    if (!rail.hasAttribute('tabindex')) rail.tabIndex = 0;
    if (!rail.hasAttribute('aria-label') && heading?.textContent?.trim()) {
      rail.setAttribute('aria-label', heading.textContent.trim());
    }

    let direction = 1;
    let hovered = false;
    let focused = false;
    let pointerActive = false;
    let pauseUntil = performance.now() + INITIAL_RELATED_PAUSE_MS;
    let visible = true;
    let lastTime = performance.now();

    const maxScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);

    const pauseForInteraction = (duration = USER_PAUSE_MS) => {
      pauseUntil = Math.max(pauseUntil, performance.now() + duration);
    };

    const currentCardIndex = () => {
      const items = cards();
      if (!items.length) return 0;

      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      const left = rail.scrollLeft;

      items.forEach((card, index) => {
        const delta = Math.abs(card.offsetLeft - left);
        if (delta < distance) {
          distance = delta;
          nearest = index;
        }
      });

      return nearest;
    };

    const updateUI = () => {
      const items = cards();
      const total = items.length;
      const max = maxScroll();
      const index = currentCardIndex();

      if (current) current.textContent = pad(total ? index + 1 : 0);
      if (totalNode) totalNode.textContent = pad(total);
      if (progress) progress.style.transform = `scaleX(${max <= 1 ? 1 : Math.min(1, Math.max(0, rail.scrollLeft / max))})`;
      if (prev) prev.disabled = max <= 1 || rail.scrollLeft <= 1;
      if (next) next.disabled = max <= 1 || rail.scrollLeft >= max - 1;
    };

    const cardStep = () => {
      const items = cards();
      if (items.length > 1) return Math.max(1, items[1].offsetLeft - items[0].offsetLeft);
      return items[0]?.offsetWidth || Math.max(1, rail.clientWidth * 0.72);
    };

    const move = amount => {
      pauseForInteraction();
      rail.scrollBy({
        left: cardStep() * amount,
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    };

    prev?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));

    rail.addEventListener('scroll', updateUI, { passive: true });
    rail.addEventListener('wheel', () => pauseForInteraction(), { passive: true });
    rail.addEventListener('touchstart', () => pauseForInteraction(), { passive: true });
    rail.addEventListener('keydown', () => pauseForInteraction());
    rail.addEventListener('click', () => pauseForInteraction(), true);

    rail.addEventListener('pointerdown', () => {
      pointerActive = true;
      rail.classList.add('is-ll-pdp-v5-interacting');
      pauseForInteraction();
    }, { passive: true });

    const finishPointer = () => {
      pointerActive = false;
      rail.classList.remove('is-ll-pdp-v5-interacting');
      pauseForInteraction();
    };

    rail.addEventListener('pointerup', finishPointer, { passive: true });
    rail.addEventListener('pointercancel', finishPointer, { passive: true });

    root.addEventListener('mouseenter', () => { hovered = true; }, { passive: true });
    root.addEventListener('mouseleave', () => { hovered = false; }, { passive: true });
    root.addEventListener('focusin', () => { focused = true; });
    root.addEventListener('focusout', event => {
      if (!root.contains(event.relatedTarget)) focused = false;
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting);
        lastTime = performance.now();
      }, { rootMargin: '160px 0px' });
      observer.observe(root);
    }

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(updateUI);
      observer.observe(rail);
    }

    const paused = now => (
      reducedMotion.matches ||
      document.hidden ||
      !visible ||
      hovered ||
      focused ||
      pointerActive ||
      now < pauseUntil
    );

    const tick = now => {
      if (!root.isConnected) return;

      const delta = Math.min(32, now - lastTime);
      lastTime = now;
      const max = maxScroll();

      if (max > 2 && !paused(now)) {
        let target = rail.scrollLeft + direction * RELATED_SPEED * (delta / 1000);

        if (target >= max) {
          target = max;
          direction = -1;
        } else if (target <= 0) {
          target = 0;
          direction = 1;
        }

        rail.scrollLeft = target;
      }

      updateUI();
      requestAnimationFrame(tick);
    };

    document.addEventListener('visibilitychange', () => {
      lastTime = performance.now();
    });

    reducedMotion.addEventListener?.('change', () => {
      lastTime = performance.now();
    });

    updateUI();
    requestAnimationFrame(tick);
  };

  const bindRelatedRoots = (scope = document) => {
    if (scope.matches?.(RELATED_ROOT)) bindRelatedRail(scope);
    scope.querySelectorAll?.(RELATED_ROOT).forEach(bindRelatedRail);
  };

  const watchAsyncRecommendations = () => {
    document.querySelectorAll(RELATED_ROOT).forEach(root => {
      if (root.querySelector(RELATED_RAIL)?.querySelector(RELATED_CARD)) {
        bindRelatedRail(root);
        return;
      }

      if (root.dataset.llPdpV51Observer === 'true') return;
      root.dataset.llPdpV51Observer = 'true';

      const observer = new MutationObserver(() => {
        if (!root.querySelector(RELATED_RAIL)?.querySelector(RELATED_CARD)) return;
        observer.disconnect();
        delete root.dataset.llPdpV51Observer;
        bindRelatedRail(root);
      });

      observer.observe(root, { childList: true, subtree: true });
    });
  };

  const boot = (scope = document) => {
    syncSizeGuideLabels(scope);
    syncAllGalleries();
    bindRelatedRoots(scope);
    watchAsyncRecommendations();
  };

  document.addEventListener('llcl:variant-media-state', event => {
    scheduleGallerySync(event.target?.closest?.(PRODUCT_SECTION) || document.querySelector(PRODUCT_SECTION));
  });

  document.addEventListener('variant:change', event => {
    scheduleGallerySync(event.target?.closest?.(PRODUCT_SECTION) || document.querySelector(PRODUCT_SECTION));
  });

  document.addEventListener('product:rerender', event => {
    scheduleGallerySync(event.target?.closest?.(PRODUCT_SECTION) || document.querySelector(PRODUCT_SECTION));
    syncSizeGuideLabels(document);
    watchAsyncRecommendations();
  });

  document.addEventListener('shopify:section:load', event => {
    boot(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }
})();
